import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHover } from 'components/Editor/hooks/useHover';

import { toggleEditMode, updateWidgetProperty } from 'actions/editor';
import { updateWidgetContent } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';
import { EditorContext } from '../../index';

import { Button } from 'react-bootstrap';
import { Property } from '../../components/properties/';
import { LiveElementCreationProperty } from '../../components/liveElements';
import { getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType } from 'components/Editor/helper/widget';

import { GROUP, HOTSPOT, PAGE, SECTION_COLUMN } from 'constants/widgetTypes';

import styles from './iframe.module.scss';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';

const IFrameWidgetComponent = ({ className, style, widgetProps, widgetRef }) => {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  
  const url = _.get(widgetProps, 'content');
  const empty = !(url && url.trim().length > 0);

  var widgetStyle = {
    ...style
  }

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.iframe, { [styles.empty]: empty })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      {empty &&
        <div>Please enter a valid url</div>
      }

      {!empty &&
        <iframe sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-pointer-lock"
          src={url}
        ></iframe>
      }

    </div>
  )
};

const iFrameWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const IFrameWidget = React.memo(IFrameWidgetComponent, iFrameWidgetEquals);

const HtmlWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const template = _.get(props, 'template.widget') != null;

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }
  const handleLiveElement = () => {
    setView('liveElements')
  }

  return (
    <>
      <Property.Action onClick={handleEditMode}>Edit URL</Property.Action>

      {!template &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => setView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {editMode &&
        <HtmlSourceEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'EMBED'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const HtmlSourceEditor = (props) => {
  const dispatch = useDispatch();

  const [inputUrl, setInputUrl] = useState(_.get(props, `widget.content`) || "");
  const [error, setError] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const id = _.get(props, 'widget.uid');
  const url = _.get(props, `widget.content`) || "";

  useEffect(() => {
    setInputUrl(url || "");
  }, [url]);

  const validUrl = (value) => {
    if (!value || value.trim().length === 0) {
      return "Please enter source url";
    }

    if (!(value.startsWith('http://') || value.startsWith('https://'))) {
      return "Url should begin with http or https";
    }

    try {
      const urlObject = new URL(value);
    } catch (e) {
      return "Please enter source url";
    }

    return null;
  }

  const handleChange = (e) => {
    var value = e.target.value;
    setInputUrl(e.target.value);

    const error = validUrl(value);
    setError(error);
  }

  const handleSave = () => {
    dispatch(updateWidgetContent(editorSession, id, inputUrl));
  }

  return (
    <Property.Dialog
      width={222}
      target={`#${id}`}
      title='Edit URL'
      onCancel={props.onCancel}
    >
      <Property name='Enter your source url here!' className={styles.urlProperty}>
        <Property.Input value={inputUrl}
          onChange={handleChange}
          placeholder="Enter Url"
          error={error} />
      </Property>
      <div className={styles.buttonTray}>
        <Button className={styles.save}
          disabled={error != null}
          onClick={handleSave}
        >Save Changes</Button>
      </div>
    </Property.Dialog>
  );
}

IFrameWidget.moveableConfig = (editorSession, widget) => {
  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    cloneable: true,
    deletable: true,
    renderDirections: ["s", "se", "sw", "e", "w"],
    onReparent: (widget, parent) => {
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT) {
        return false;
      }

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN].includes(parentType);

      if (group || denyHostpotReparent) {
        return false;
      }

      return true;
    }
  }
}

IFrameWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <HtmlWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <HtmlWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

IFrameWidget.processRequest = (widget) => {
  return widget;
}

export { IFrameWidget };
